import { useEffect, useState } from "react";
import {
  Container,
  Title,
  TextInput,
  Button,
  Text,
  Stack,
  MultiSelect,
  Loader,
  Textarea,
  Select,
  TagsInput,
  Paper,
  List,
  Badge,
  Group,
} from "@mantine/core";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import { marketplaceSyncBadge } from "./marketplaceSync";
import {
  acceptCatalogGeneration,
  createMarketplace,
  discardCatalogGeneration,
  getMarketplace,
  revertCatalogGeneration,
  triggerMarketplaceRefresh,
  updateMarketplace,
} from "../api";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useTenants } from "../hooks/useTenants";

function emptyExclusion() {
  return { rootCodes: [], labelPrefixes: [], codeSubstrings: [] };
}

function formExclusion(exclusion) {
  return {
    rootCodes: exclusion?.rootCodes ?? [],
    labelPrefixes: exclusion?.labelPrefixes ?? [],
    codeSubstrings: exclusion?.codeSubstrings ?? [],
  };
}

function CatalogGenerationPanel({ marketplace, busy, onChange, onBusy }) {
  const generation = marketplace.latestCatalogGeneration;
  const canRevert = Boolean(
    marketplace.activeCatalogGenerationId || marketplace.previousCatalogGenerationId
  );
  const status = generation?.status;
  const canAccept =
    generation &&
    (status === "suspect" ||
      (status === "accepted" && generation.id !== marketplace.activeCatalogGenerationId));
  const canDiscard = status === "suspect";

  const run = (action) => {
    onBusy(true);
    action()
      .then((data) => {
        if (data) onChange(data);
      })
      .catch((error) => {
        notifications.show({
          title: "Catalog update was not changed",
          message: error?.message || "Request failed",
          color: "red",
        });
      })
      .finally(() => onBusy(false));
  };

  if (!generation && !canRevert) return null;

  return (
    <Stack gap="xs" mt="md">
      {generation && (
        <>
          <Text size="sm">Latest catalog update: {generation.status}</Text>
          <Text size="sm">
            Hierarchies: {generation.seenHierarchies} / {generation.baselineHierarchies}
          </Text>
          <Text size="sm">
            Attributes: {generation.seenAttributes} / {generation.baselineAttributes}
          </Text>
          <Text size="sm">
            Value lists: {generation.seenValueLists} / {generation.baselineValueLists}
          </Text>
          {generation.reason && (
            <Text size="sm" c="dimmed">
              {generation.reason}
            </Text>
          )}
        </>
      )}
      <Group>
        {canAccept && (
          <Button
            size="xs"
            variant="default"
            loading={busy}
            onClick={() => run(() => acceptCatalogGeneration(marketplace.id, generation.id))}
          >
            Accept this catalog
          </Button>
        )}
        {canDiscard && (
          <Button
            size="xs"
            variant="default"
            loading={busy}
            onClick={() => run(() => discardCatalogGeneration(marketplace.id, generation.id))}
          >
            Discard this catalog
          </Button>
        )}
        {canRevert && (
          <Button
            size="xs"
            variant="default"
            loading={busy}
            onClick={() => run(() => revertCatalogGeneration(marketplace.id))}
          >
            Use previous catalog
          </Button>
        )}
      </Group>
    </Stack>
  );
}

function exclusionOrNull(exclusion) {
  const normalized = formExclusion(exclusion);
  if (
    normalized.rootCodes.length === 0 &&
    normalized.labelPrefixes.length === 0 &&
    normalized.codeSubstrings.length === 0
  ) {
    return null;
  }
  return normalized;
}

export default function MarketplaceEdit({ params }) {
  const [isFetching, setIsFetching] = useState(false);
  const [mp, setMp] = useState(null);

  // Tenants hook (stable references)
  const {
    options: tenantOptions,
    isLoading: isTenantsLoading,
    refetch: refetchTenants,
  } = useTenants();

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      id: "",
      apiKey: "",
      baseUrl: "",
      pathSeparator: "/",
      promptIntro: "",
      rootTypeCode: "",
      categoryExportType: "leafOnly",
      tenantWhitelist: null,
      categoryExclusion: emptyExclusion(),
    },
    transformValues: (values) => ({
      ...values,
      // Convert empty array to null, otherwise keep the array
      tenantWhitelist:
        values.tenantWhitelist && values.tenantWhitelist.length > 0
          ? values.tenantWhitelist
          : null,
      categoryExclusion: exclusionOrNull(values.categoryExclusion),
    }),
  });

  useEffect(() => {
    // Tenants fetched via hook; ensure refetch in case
    refetchTenants().catch((error) => {
      console.error("Failed to load tenants:", error);
    });

    if (params.id) {
      getMarketplace(params.id).then((data) => {
        console.log("Loaded marketplace data:", data);
        setMp(data);
        const withExclusion = {
          ...data,
          categoryExclusion: formExclusion(data.categoryExclusion),
        };
        form.setInitialValues(withExclusion);
        form.setValues(withExclusion);
      });
    }
  }, []);

  const sync = mp ? marketplaceSyncBadge(mp) : null;

  return (
    <Container size="sm">
      <Title order={1}>{form.getValues()["name"]}</Title>
      <Text c="dimmed">{params.id}</Text>

      {/* <Code block>
        <pre>{JSON.stringify(form.values, null, 2)}</pre>
      </Code> */}

      <form
        onSubmit={form.onSubmit(async (values) => {
          setIsFetching(true);
          const data = params.id
            ? await updateMarketplace(params.id, values)
            : await createMarketplace(values);
          setIsFetching(false);
          const withExclusion = {
            ...data,
            categoryExclusion: formExclusion(data.categoryExclusion),
          };
          form.setValues(withExclusion);
          setMp((prev) => ({
            ...prev,
            ...withExclusion,
            latestFetches: prev?.latestFetches ?? data.latestFetches,
          }));
          notifications.show({
            title: "Changes Saved!",
            color: "green",
          });
        })}
      >
        <Stack>
          <TextInput label="Name" {...form.getInputProps("name")} />
          <TextInput label="API Key" {...form.getInputProps("apiKey")} />
          <TextInput label="Base URL" {...form.getInputProps("baseUrl")} />

          <TextInput
            label="Root Type Code"
            description="Sellable tree root. Search, embeddings, and the types page use this node and its descendants only. Nodes outside it are not category candidates. B&Q is 3 (Marketplace Categories), not the 1P dump."
            placeholder="3"
            {...form.getInputProps("rootTypeCode")}
          />

          <Select
            label="Path Separator"
            data={["/", "."]}
            value={form.values.pathSeparator}
            onChange={(val) => form.setFieldValue("pathSeparator", val)}
          />

          <Select
            label="Category Export Type"
            data={["leafOnly", "fullPath"]}
            value={form.values.categoryExportType}
            onChange={(val) => form.setFieldValue("categoryExportType", val)}
          />

          <Textarea
            autosize
            minRows={3}
            maxRows={5}
            label="Prompt Intro"
            {...form.getInputProps("promptIntro")}
          />

          <TagsInput
            label="Excluded category roots"
            description="Extra drops inside the Root Type Code tree. Leave empty to use the defaults (labels that start with DO NOT USE, codes that contain RESTRICTED). A saved list replaces the defaults. A root code excludes that node and every child. The next marketplace sync or optivise run applies the change."
            placeholder="1P_ROOT"
            {...form.getInputProps("categoryExclusion.rootCodes")}
          />
          <TagsInput
            label="Excluded label prefixes"
            placeholder="DO NOT USE"
            {...form.getInputProps("categoryExclusion.labelPrefixes")}
          />
          <TagsInput
            label="Excluded code fragments"
            placeholder="RESTRICTED"
            {...form.getInputProps("categoryExclusion.codeSubstrings")}
          />

          <MultiSelect
            label="Tenant Whitelist"
            description="If set, only these tenants can access this marketplace. Leave empty to allow all tenants."
            placeholder={
              isTenantsLoading ? "Loading tenants..." : "Select tenants"
            }
            data={tenantOptions}
            searchable
            clearable
            disabled={isTenantsLoading}
            rightSection={isTenantsLoading ? <Loader size="xs" /> : null}
            {...form.getInputProps("tenantWhitelist")}
            // Ensure value is an array for MultiSelect (Mantine expects [])
            value={form.values.tenantWhitelist || []}
            onChange={(val) => {
              // Convert empty array back to null for form state
              form.setFieldValue(
                "tenantWhitelist",
                val && val.length > 0 ? val : null
              );
            }}
          />

          <Button mt="lg" loading={isFetching} type="submit">
            Save
          </Button>
        </Stack>
      </form>

      {mp && params.id && sync && (
        <Paper my="md" p="md" withBorder>
          <Group justify="space-between" align="center">
            <Title order={3}>Sync</Title>
            <Badge color={sync.color}>{sync.label}</Badge>
          </Group>
          <Text size="sm" mt="sm">
            Last successful update:{" "}
            {mp.lastSuccessfulUpdateAt ? (
              <Timeago date={mp.lastSuccessfulUpdateAt} />
            ) : (
              "never"
            )}
          </Text>
          <Text size="sm">
            Last attempt:{" "}
            {mp.lastUpdateAttemptAt ? (
              <Timeago date={mp.lastUpdateAttemptAt} />
            ) : (
              "never"
            )}
          </Text>
          {mp.lastUpdateError && (
            <Text size="sm" c="red" mt="xs">
              {mp.lastUpdateError}
            </Text>
          )}
          <CatalogGenerationPanel
            marketplace={mp}
            busy={isFetching}
            onChange={(data) => setMp(data)}
            onBusy={setIsFetching}
          />
        </Paper>
      )}

      {mp?.latestFetches && (
        <Paper my="md">
          <Title order={3}>Latest Fetches</Title>
          <List>
            {mp.latestFetches.map((fetch) => (
              <List.Item key={fetch.id}>
                <Text>{fetch.status}</Text>
                INS: <Text>{fetch.insertCount}</Text>
                MOD: <Text>{fetch.modificationCount}</Text>
                ERR: <Text>{fetch.errorMessage}</Text>
              </List.Item>
            ))}
          </List>
        </Paper>
      )}

      <Button
        mt="xl"
        variant="default"
        onClick={() =>
          triggerMarketplaceRefresh(params.id)
            .then(() => {
              notifications.show({
                title: "Marketplace Refresh Triggered",
                color: "blue",
              });
              return getMarketplace(params.id);
            })
            .then((data) => {
              if (data) setMp(data);
            })
            .catch(() => {})
        }
      >
        Trigger full attribute update
      </Button>
    </Container>
  );
}
